import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useLayoutEffect, lazy, Suspense, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';
import { apiService } from '@/lib/api';
import { isNative } from '@/lib/capacitor';
import { useIsMobile } from '@/hooks/useIsMobile';
import { OfflineBoundary } from './OfflineBoundary';
import {
  Briefcase,
  DollarSign,
  PhoneCall,
  Users,
  Settings,
  Menu,
  X,
  MapPin,
  Phone,
  Receipt,
  Mail,
  Calendar,
  FileText,
  ClipboardList,
  Package,
  MessageCircle,
  Shield,
  LayoutDashboard,
  Plus,
  ChevronsLeft,
  ChevronsRight,
  Building2,
  Clock,
  Columns,
  Wallet
} from 'lucide-react';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';
import { useVoipUnreadCount } from '@/hooks/useVoipUnreadCount';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubscription } from '@/hooks/useSubscription';
import { SubscriptionLockedScreen } from '@/components/SubscriptionLockedScreen';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { canAddCompany } from '@/lib/subscriptionCopy';
import { updateAppHistory } from '@/lib/navigation';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { CustomSelect } from './ui/CustomSelect';
import { NewJobModal } from '@/components/NewJobModal';
import { DialerModal } from '@/components/DialerModal';
import { TimezoneIndicator } from '@/components/ui/TimezoneIndicator';
import { NotificationBell } from '@/components/NotificationBell';
import { ProfileMenu } from '@/components/ProfileMenu';
import { NewActionButton } from '@/components/NewActionButton';
import { subscribeOpenEstimateRequests, subscribeOpenJobRequests } from '@/lib/notificationCenter';
import { SecondaryPaneContext, SplitScreenAreaContext } from '@/lib/splitScreen';

/**
 * Top-bar page title: 16px, stepped down only when the title doesn't fit the space left of the
 * icons. Re-fits on resize and when a sibling (bell, timezone) changes width.
 */
function TopBarTitle({ text }: { text?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      let size = 16;
      el.style.fontSize = `${size}px`;
      // ponytail: 11px floor; past that the ellipsis takes over rather than unreadable text
      while (el.scrollWidth > el.clientWidth && size > 11) el.style.fontSize = `${--size}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);
  return (
    // Fixed line height so a font-size step doesn't resize the box and re-trigger the observer.
    <div ref={ref} className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap leading-6 font-semibold text-gray-900">
      {text}
    </div>
  );
}

function ScrollableNav({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLElement>(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const checkScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setIsAtTop(scrollTop <= 0);
      setIsAtBottom(Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1);
    }
  }, []);

  useEffect(() => {
    checkScroll();
    const observer = new ResizeObserver(() => checkScroll());
    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }
    
    window.addEventListener('resize', checkScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, children]);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      {!isAtTop && (
        <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-slate-50 to-transparent pointer-events-none z-10" />
      )}
      <nav
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex-1 overflow-y-auto space-y-0.5 px-3 py-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
      >
        {children}
      </nav>
      {!isAtBottom && (
        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none z-10 flex items-end justify-center pb-2">
           <span className="text-[10px] text-slate-500 font-medium uppercase tracking-widest bg-white/90 px-2 py-0.5 rounded-full animate-pulse shadow-sm border border-slate-200">More</span>
        </div>
      )}
    </div>
  );
}

/** Maps tab IDs to their corresponding feature keys. Tabs not listed are either always visible or admin-only. */
const TAB_FEATURE_KEY_MAP: Partial<Record<TabType, string>> = {
  jobs: 'jobs',
  leads: 'leads',
  // "messages" is now an empty umbrella over messages.internal (team chat) and messages.sms,
  // so that a plan can exclude internal chat while still allowing templated SMS.
  messaging: 'messages.internal',
  contacts: 'contacts',
  invoices: 'invoices',
  estimates: 'estimates',
  email: 'email',
  map: 'map',
  technicians: 'technicians',
  'payment-provider': 'payments',
  reports: 'reports',
  expenses: 'expenses',
  dashboard: 'dashboard',
  voip: 'voip.history',
};

// Lazy load page components for code splitting
const JobsPage = lazy(() => import('./JobsPage').then(module => ({ default: module.JobsPage })));
const LeadsPage = lazy(() => import('./LeadsPage').then(module => ({ default: module.LeadsPage })));
const TechniciansPage = lazy(() => import('./TechniciansPage').then(module => ({ default: module.TechniciansPage })));
const ProfilePage = lazy(() => import('./ProfilePage').then(module => ({ default: module.ProfilePage })));
const NotificationsPage = lazy(() => import('./NotificationsPage'));
const ContactsPage = lazy(() => import('./ContactsPage').then(module => ({ default: module.ContactsPage })));
const InvoicesPage = lazy(() => import('./InvoicesPage').then(module => ({ default: module.InvoicesPage })));
const EmailPage = lazy(() => import('./EmailPage').then(module => ({ default: module.EmailPage })));
const CalendarPage = lazy(() => import('./CalendarPage').then(module => ({ default: module.CalendarPage })));
const MapPage = lazy(() => import('./MapPage').then(module => ({ default: module.MapPage })));
const ReportsPage = lazy(() => import('./ReportsPage').then(module => ({ default: module.ReportsPage })));
const ExpensesPage = lazy(() => import('./ExpensesPage').then(module => ({ default: module.ExpensesPage })));
const EstimatesPage = lazy(() => import('./EstimatesPage').then(module => ({ default: module.EstimatesPage })));
const PaymentProviderPage = lazy(() => import('./PaymentProviderPage').then(module => ({ default: module.PaymentProviderPage })));
const MessagingPage = lazy(() => import('./MessagingPage').then(module => ({ default: module.MessagingPage })));
const SettingsPage = lazy(() => import('./SettingsPage').then(module => ({ default: module.SettingsPage })));
const SetupWizardModal = lazy(() => import('./setup/SetupWizardModal').then(module => ({ default: module.SetupWizardModal })));
const PermissionsPage = lazy(() => import('./PermissionsPage').then(module => ({ default: module.PermissionsPage })));
const CompanyDashboardPage = lazy(() => import('./CompanyDashboardPage'));
const TechnicianDashboardPage = lazy(() => import('./TechnicianDashboardPage').then(module => ({ default: module.TechnicianDashboardPage })));
const ServiceCatalogContent = lazy(() => import('./ServiceCatalogContent').then(module => ({ default: module.ServiceCatalogContent })));
const VoipCallHistoryPage = lazy(() => import('./VoipCallHistoryPage').then(module => ({ default: module.VoipCallHistoryPage })));
type TabType = 'jobs' | 'leads' | 'messaging' | 'contacts' | 'invoices' | 'estimates' | 'technicians' | 'profile' | 'email' | 'calendar' | 'map' | 'payment-provider' | 'reports' | 'expenses' | 'settings' | 'permissions' | 'dashboard' | 'my-dashboard' | 'service-catalog' | 'voip' | 'notifications';

function parseInitialRoute(): { tab: TabType; contactId: string | null } {
  const path = window.location.pathname.toLowerCase();
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { tab: 'jobs', contactId: null };
  }

  const [first, second] = segments;

  switch (first) {
    case 'jobs':
      return { tab: 'jobs', contactId: null };
    case 'leads':
      return { tab: 'leads', contactId: second || null };
    case 'messaging':
      return { tab: 'messaging', contactId: null };
    case 'contacts':
      return { tab: 'contacts', contactId: second || null };
    case 'invoices':
      return { tab: 'invoices', contactId: null };
    case 'estimates':
      return { tab: 'estimates', contactId: null };
    case 'technicians':
      return { tab: 'technicians', contactId: null };
    case 'settings':
    case 'companies':
    case 'utils':
    case 'management':
    case 'users':
    case 'payment-settings':
      return { tab: 'settings', contactId: null };
    case 'permissions':
      return { tab: 'permissions', contactId: null };
    case 'profile':
      return { tab: 'profile', contactId: null };
    case 'email':
      return { tab: 'email', contactId: null };
    case 'calendar':
      return { tab: 'calendar', contactId: null };
    case 'map':
      return { tab: 'map', contactId: null };
    case 'payment-provider':
      return { tab: 'payment-provider', contactId: null };
    case 'reports':
      return { tab: 'reports', contactId: null };
    case 'expenses':
      return { tab: 'expenses', contactId: null };
    case 'service-catalog':
      return { tab: 'service-catalog', contactId: null };
    case 'dashboard':
      return { tab: 'dashboard', contactId: null };
    case 'my-dashboard':
      return { tab: 'my-dashboard', contactId: null };
    case 'voip':
      return { tab: 'voip', contactId: null };
    case 'notifications':
      return { tab: 'notifications', contactId: null };
    default:
      return { tab: 'jobs', contactId: null };
  }
}

function formatSidebarTime(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

const SPLIT_OPEN_KEY = 'dashboard_split_open';
const SPLIT_TAB_KEY = 'dashboard_split_tab';
const SPLIT_RATIO_KEY = 'dashboard_split_ratio';
const SIDEBAR_COLLAPSED_KEY = 'dashboard_sidebar_collapsed';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the split still works until the page reloads.
  }
}

/** True on screens wide enough for split screen: 1440px+, so each side gets ~590px next to the menu. */
function useWideScreen() {
  const query = '(min-width: 1440px)';
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return matches;
}

export function Dashboard() {
  const initialRoute = parseInitialRoute();
  const [activeTab, setActiveTab] = useState<TabType>(initialRoute.tab);
  const [contactsRouteId, setContactsRouteId] = useState<string | null>(initialRoute.contactId);
  const [leadsRouteId, setLeadsRouteId] = useState<string | null>(initialRoute.tab === 'leads' ? initialRoute.contactId : null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // xl+ sidebar folded down to an icon rail. Remembered in this browser.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readStored(SIDEBAR_COLLAPSED_KEY) === '1');
  // Lets full-page overlays that sit beside the sidebar (e.g. JobDetailsDesktop) follow its width.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', sidebarCollapsed ? '4rem' : '16rem');
  }, [sidebarCollapsed]);
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      writeStored(SIDEBAR_COLLAPSED_KEY, prev ? '0' : '1');
      return !prev;
    });
  };
  // Split screen (wide screens only): a second page on the right with its own page picker.
  // Remembered in this browser.
  const isWideScreen = useWideScreen();
  const [splitOpen, setSplitOpen] = useState<boolean>(() => readStored(SPLIT_OPEN_KEY) === '1');
  const [splitTab, setSplitTab] = useState<TabType>(() => (readStored(SPLIT_TAB_KEY) as TabType | null) || 'calendar');
  useEffect(() => { writeStored(SPLIT_OPEN_KEY, splitOpen ? '1' : '0'); }, [splitOpen]);
  useEffect(() => { writeStored(SPLIT_TAB_KEY, splitTab); }, [splitTab]);
  // Each split side's box; a job opened from a side covers just that side.
  const primaryPaneRef = useRef<HTMLDivElement>(null);
  const secondaryPaneRef = useRef<HTMLDivElement>(null);
  // Left side's share of the width, set by dragging the divider. Kept between 25% and 75% so both
  // pages stay usable, and remembered in this browser.
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    const stored = Number(readStored(SPLIT_RATIO_KEY));
    return stored >= 0.25 && stored <= 0.75 ? stored : 0.5;
  });
  const [isDraggingSplit, setIsDraggingSplit] = useState(false);
  const splitTopBar = isWideScreen && splitOpen;
  useEffect(() => { writeStored(SPLIT_RATIO_KEY, String(splitRatio)); }, [splitRatio]);
  const startSplitDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = splitContainerRef.current;
    if (!container) return;
    // No preventDefault: it suppresses the follow-up mousedown, and open dropdowns close on
    // outside mousedown. Text selection is blocked by userSelect below instead.
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    setIsDraggingSplit(true);
    document.body.style.userSelect = 'none';
    const onMove = (moveEvent: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const ratio = (moveEvent.clientX - rect.left) / rect.width;
      setSplitRatio(Math.min(0.75, Math.max(0.25, ratio)));
    };
    const onUp = () => {
      setIsDraggingSplit(false);
      document.body.style.userSelect = '';
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const historyModeRef = useRef<'replace' | 'push' | 'skip'>('replace');
  const { signOut } = useAuth();
  const { isAdmin, userRole, isLoading: roleLoading } = useCurrentUser();
  const isMobile = useIsMobile();
  // The technician dashboard is a mobile feature (app or mobile browser); hidden on desktop.
  const showTechnicianDashboard = userRole === 'technician' && isMobile;
  // Opening the app at "/" lands technicians on their dashboard. The role isn't known on the
  // first render, so hold the page until it is instead of flashing Jobs first.
  const [pendingRoleLanding, setPendingRoleLanding] = useState(() => window.location.pathname === '/');
  const unreadMessageCount = useUnreadMessageCount(activeTab);
  const voipUnread = useVoipUnreadCount(activeTab);
  const { addNotification } = useNotifications();
  const { companies, selectedCompanyId, setSelectedCompanyId, selectedCompany, isLoading: companiesLoading, companyTimezone, sortCompanies } = useGlobalCompany();
  const { isFeatureAllowed, isLoading: permissionsLoading } = usePermissions();
  const { openJob, closeJob, openEstimate, closeEstimate } = useVoiceCall();
  const { isLocked, subscription } = useSubscription();
  const queryClient = useQueryClient();
  const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  // Stable: DialerModal closes itself from an effect that depends on onClose.
  const closeDialer = useCallback(() => setIsDialerOpen(false), []);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { selectedOrg } = useGlobalOrganization();
  const [autoSetupWizard, setAutoSetupWizard] = useState(false);

  // A new organization's admin gets the setup wizard once, on their first desktop-web visit. It is
  // recorded as shown when it opens, so closing it, refreshing or leaving for Stripe/Gmail doesn't
  // bring it back; the Settings button still does. Never on a phone or tablet (app or browser), and
  // not recorded there either, so the first desktop visit still gets it. Strictly null: an older
  // backend omits the field.
  useEffect(() => {
    if (!isAdmin || isLocked || isMobile || isNative() || selectedOrg?.setup_wizard_shown_at !== null) return;
    setAutoSetupWizard(true);
    apiService.markSetupWizardShown()
      .then(() => queryClient.invalidateQueries({ queryKey: ['my-organizations'] }))
      .catch(error => clientLogger.error('[Dashboard] Failed to mark setup wizard as shown:', error));
  }, [isAdmin, isLocked, isMobile, selectedOrg, queryClient]);

  const companyOptions = useMemo(() => {
    const scopedCompanyOptions = sortCompanies(companies).map((company) => ({
      value: company.id,
      label: company.name,
    }));

    if (!isAdmin) {
      return scopedCompanyOptions;
    }

    return [
      { value: '', label: 'All Companies' },
      ...scopedCompanyOptions,
    ];
  }, [companies, isAdmin, sortCompanies]);

  // ponytail: a plan-capped org gets a label, not a picker — the dropdown could only ever hold
  // one option. Orgs that kept extra companies through a downgrade still switch between them.
  const showCompanySelector = companies.length > 1 || canAddCompany(subscription, companies.length);

  // No picker means no way back from the admin "All Companies" scope (x-company-id: global), and
  // the label would name a company the requests aren't scoped to. Pin it to the one company.
  useEffect(() => {
    if (showCompanySelector || companiesLoading || companies.length === 0) return;
    if (selectedCompanyId !== companies[0].id) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [showCompanySelector, companiesLoading, companies, selectedCompanyId, setSelectedCompanyId]);

  const companyPicker = showCompanySelector ? (
    <CustomSelect
      value={selectedCompanyId}
      onChange={(value) => {
        if (!isAdmin && !value) {
          return;
        }
        setSelectedCompanyId(value);
      }}
      options={companyOptions}
      placeholder={isAdmin ? 'All Companies' : 'Select company'}
      disabled={companiesLoading}
    />
  ) : (
    <div className="flex items-center gap-2.5 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
        <Building2 className="h-4 w-4 text-slate-600" />
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Company</span>
        <span className="block truncate text-sm font-semibold text-slate-900">
          {selectedCompany?.name ?? companies[0]?.name ?? ''}
        </span>
      </span>
    </div>
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  const localTime = useMemo(() => formatSidebarTime(currentTime), [currentTime]);
  const companyTime = useMemo(() => {
    const tz = companyTimezone?.trim();
    if (!tz) {
      return null;
    }

    return formatSidebarTime(currentTime, tz);
  }, [currentTime, companyTimezone]);

  const showCompanyTime = !!companyTime && companyTime !== localTime;

  // Pages that were NOT migrated to company timezone still display local time
  const LOCAL_TZ_TABS: TabType[] = ['messaging', 'email'];
  const tzIndicatorMode = LOCAL_TZ_TABS.includes(activeTab) ? 'local' : 'company';

  useEffect(() => {
    if (!pendingRoleLanding || roleLoading) return;
    if (showTechnicianDashboard) {
      historyModeRef.current = 'replace';
      setActiveTab('my-dashboard');
    }
    setPendingRoleLanding(false);
  }, [pendingRoleLanding, roleLoading, showTechnicianDashboard]);

  useEffect(() => {
    // The technician dashboard is role-scoped: everyone else goes to jobs
    if (activeTab === 'my-dashboard' && !showTechnicianDashboard && !roleLoading) {
      historyModeRef.current = 'replace';
      setActiveTab('jobs');
      return;
    }
    // If user tries to access admin-only tabs but is not admin, redirect to jobs
    if ((activeTab === 'settings' || activeTab === 'permissions') && !isAdmin && !roleLoading) {
      historyModeRef.current = 'replace';
      setActiveTab('jobs');
      return;
    }
    // If user tries to access a feature-gated tab they don't have permission for, redirect to jobs.
    // This deliberately includes admins: they bypass role and override checks, but not the
    // organization's subscription plan, so an admin can land on a tab their plan excludes.
    if (!roleLoading && !permissionsLoading) {
      const featureKey = TAB_FEATURE_KEY_MAP[activeTab];
      if (featureKey && !isFeatureAllowed(featureKey)) {
        historyModeRef.current = 'replace';
        setActiveTab('jobs');
      }
    }
  }, [activeTab, isAdmin, showTechnicianDashboard, roleLoading, permissionsLoading, isFeatureAllowed]);

  useEffect(() => {
    const handlePopState = () => {
      const next = parseInitialRoute();
      historyModeRef.current = 'skip';
      setActiveTab(next.tab);
      setContactsRouteId(next.tab === 'contacts' ? next.contactId : null);
      setLeadsRouteId(next.tab === 'leads' ? next.contactId : null);
    };

    // Handle custom navigation events (for programmatic navigation)
    const handleNavigation = () => {
      const next = parseInitialRoute();
      historyModeRef.current = 'skip';
      setActiveTab(next.tab);
      setContactsRouteId(next.tab === 'contacts' ? next.contactId : null);
      setLeadsRouteId(next.tab === 'leads' ? next.contactId : null);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigation', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigation', handleNavigation);
    };
  }, []);

  useEffect(() => {
    let path = '/';
    switch (activeTab) {
      case 'jobs':
        path = '/jobs';
        break;
      case 'leads':
        path = leadsRouteId ? `/leads/${leadsRouteId}` : '/leads';
        break;
      case 'contacts':
        path = contactsRouteId ? `/contacts/${contactsRouteId}` : '/contacts';
        break;
      case 'invoices':
        path = '/invoices';
        break;
      case 'estimates':
        path = '/estimates';
        break;
      case 'technicians':
        path = '/technicians';
        break;
      case 'settings':
        path = '/settings';
        break;
      case 'permissions':
        path = '/permissions';
        break;
      case 'profile':
        path = '/profile';
        break;
      case 'calendar':
        path = '/calendar';
        break;
      case 'email':
        path = '/email';
        break;
      case 'map':
        path = '/map';
        break;
      case 'messaging':
        path = '/messaging';
        break;
      case 'payment-provider':
        path = '/payment-provider';
        break;
      case 'reports':
        path = '/reports';
        break;
      case 'expenses':
        path = '/expenses';
        break;
      case 'dashboard':
        path = '/dashboard';
        break;
      case 'my-dashboard':
        path = '/my-dashboard';
        break;
      case 'service-catalog':
        path = '/service-catalog';
        break;
      case 'voip':
        path = '/voip';
        break;
      case 'notifications':
        path = '/notifications';
        break;
      default:
        path = '/jobs';
    }

    if (historyModeRef.current === 'skip') {
      historyModeRef.current = 'replace';
      return;
    }

    // This effect also runs on mount, where it isn't navigating anywhere — it just restates the
    // current path. Keep the query string in that case, or deep links die before the page that
    // reads them mounts (?tab=billing / ?billing=success are consumed by lazy children, which
    // mount after this runs). A real tab change drops the previous page's params, as it should.
    const url = path === window.location.pathname ? path + window.location.search : path;

    if (historyModeRef.current === 'push') {
      updateAppHistory(url, 'push');
    } else {
      updateAppHistory(url, 'replace');
    }

    historyModeRef.current = 'replace';
  }, [activeTab, contactsRouteId, leadsRouteId]);

  // Job/estimate overlays opened via useVoiceCall() (messages, conversions, call widget) are mounted
  // above the router, so switching tabs leaves them covering the new page. Close them on navigation.
  useEffect(() => {
    closeJob();
    closeEstimate();
  }, [activeTab, closeJob, closeEstimate]);

  // Jobs/estimates opened from a notification (bell, notifications page, push tap). Declared after the
  // close-on-navigation effect so a push tap that cold-started the app isn't closed on mount.
  useEffect(() => subscribeOpenJobRequests(openJob), [openJob]);
  useEffect(() => subscribeOpenEstimateRequests(openEstimate), [openEstimate]);

  const confirmLogout = async () => {
    try {
      setLoggingOut(true);
      await signOut();
    } catch (error) {
      clientLogger.error('Logout failed:', error);
      addNotification(notificationService.error('Failed to logout'));
      throw error;
    } finally {
      setLoggingOut(false);
    }
  };

  const allTabs = [
    // `group` splits the sidebar into sections with a thin divider between them; `color` tints the icon.
    ...(showTechnicianDashboard ? [{ id: 'my-dashboard' as TabType, label: 'Dashboard', icon: LayoutDashboard, color: 'text-indigo-500', group: 0 }] : []),
    { id: 'dashboard' as TabType, label: 'Dashboard', icon: LayoutDashboard, color: 'text-indigo-500', group: 0 },
    { id: 'jobs' as TabType, label: 'Jobs', icon: Briefcase, color: 'text-orange-500', group: 0 },
    { id: 'leads' as TabType, label: 'Leads', icon: ClipboardList, color: 'text-blue-500', group: 0 },
    { id: 'calendar' as TabType, label: 'Calendar', icon: Calendar, color: 'text-emerald-500', group: 0 },
    { id: 'map' as TabType, label: 'Map', icon: MapPin, color: 'text-sky-500', group: 0 },
    { id: 'messaging' as TabType, label: 'Messages', icon: MessageCircle, color: 'text-purple-500', group: 0 },
    { id: 'voip' as TabType, label: 'Calls', icon: PhoneCall, color: 'text-amber-500', group: 0 },
    { id: 'invoices' as TabType, label: 'Invoices', icon: Receipt, color: 'text-blue-600', group: 1 },
    { id: 'estimates' as TabType, label: 'Estimates', icon: ClipboardList, color: 'text-indigo-500', group: 1 },
    { id: 'contacts' as TabType, label: 'Contacts', icon: Phone, color: 'text-teal-500', group: 1 },
    { id: 'technicians' as TabType, label: 'Technicians', icon: Users, color: 'text-orange-500', group: 1 },
    { id: 'email' as TabType, label: 'Email', icon: Mail, color: 'text-fuchsia-500', group: 1 },
    { id: 'service-catalog' as TabType, label: 'Items Catalog', icon: Package, color: 'text-blue-500', group: 1 },
    { id: 'payment-provider' as TabType, label: 'Payments', icon: DollarSign, color: 'text-green-600', group: 1 },
    { id: 'reports' as TabType, label: 'Reports', icon: FileText, color: 'text-violet-500', group: 1 },
    { id: 'expenses' as TabType, label: 'Expenses', icon: Wallet, color: 'text-rose-500', group: 1 },
    ...(isAdmin ? [
      { id: 'permissions' as TabType, label: 'Permissions', icon: Shield, color: 'text-blue-600', group: 2 },
      { id: 'settings' as TabType, label: 'Settings', icon: Settings, color: 'text-slate-600', group: 2 },
    ] : []),
  ];

  // Filter tabs based on feature permissions
  const tabs = allTabs.filter(tab => {
    const featureKey = TAB_FEATURE_KEY_MAP[tab.id];
    // Tabs without a feature key mapping are always visible (calendar, admin-only tabs)
    if (!featureKey) return true;
    return isFeatureAllowed(featureKey);
  });

  const unreadBadgeCount = unreadMessageCount + voipUnread.count;

  // One page's scroll area. Messaging and Email fill the height; the others scroll.
  const renderPage = (tab: TabType, pane: 'primary' | 'secondary') => {
    const fullHeight = tab === 'messaging' || tab === 'email';
    const Wrapper = pane === 'primary' ? 'main' : 'div';
    return (
      <Wrapper className={`flex-1 min-h-0 min-w-0 overscroll-none ${fullHeight ? 'flex flex-col overflow-hidden' : 'py-3 sm:py-8 xl:py-5 overflow-y-auto'}`}>
        <div className={fullHeight ? 'flex-1 min-h-0 flex flex-col px-3 sm:px-4 lg:px-3 py-3' : 'px-3 sm:px-4 lg:px-3'}>
          <OfflineBoundary tab={tab}>
            {renderContent(tab, pane)}
          </OfflineBoundary>
        </div>
      </Wrapper>
    );
  };

  // Shared by the slide-out (phones/tablets) and persistent (xl+) sidebars.
  // `collapsed` shows icons only (label as a tooltip, unread count as a dot on the icon).
  const renderNavItems = (onPick?: () => void, collapsed = false) => tabs.map((tab, index) => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    const startsGroup = index > 0 && tabs[index - 1].group !== tab.group;
    const hasUnread = tab.id === 'messaging' && unreadBadgeCount > 0;
    return (
      <div key={tab.id}>
        {startsGroup && <div className={`my-2 border-t border-slate-200 ${collapsed ? 'mx-1' : 'mx-3'}`} />}
        <button
          onClick={() => {
            historyModeRef.current = 'push';
            setActiveTab(tab.id);
            onPick?.();
          }}
          title={collapsed ? tab.label : undefined}
          aria-label={collapsed ? tab.label : undefined}
          className={`relative w-full flex items-center py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${collapsed ? 'justify-center px-0' : 'px-3'} ${isActive
            ? 'bg-orange-50 text-orange-600 font-semibold'
            : 'text-slate-700 hover:bg-slate-200/60 hover:text-slate-900'
            }`}
        >
          {isActive && <span className="absolute left-0 inset-y-1.5 w-1 rounded-r-full bg-orange-500" />}
          {collapsed ? (
            <span className="relative">
              <Icon className={`h-5 w-5 ${tab.color}`} />
              {hasUnread && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-orange-500 ring-2 ring-slate-50" />}
            </span>
          ) : (
            <>
              <Icon className={`mr-3 h-5 w-5 ${tab.color}`} />
              {tab.label}
            </>
          )}
          {!collapsed && hasUnread && (
            <span className="ml-auto flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadBadgeCount > 9 ? '9+' : unreadBadgeCount}
            </span>
          )}
        </button>
      </div>
    );
  });

  const renderContent = (tab: TabType = activeTab, pane: 'primary' | 'secondary' = 'primary') => {
    const LoadingFallback = () => (
      <div className="text-center py-8">
        <LoadingSpinner />
        <p className="text-gray-600 mt-2">Loading...</p>
      </div>
    );

    // Wait for permissions to load before rendering page content.
    // This prevents child components from firing API calls before permissions are resolved.
    if (permissionsLoading || pendingRoleLanding) {
      return <LoadingFallback />;
    }

    switch (tab) {
      case 'jobs':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <JobsPage />
          </Suspense>
        );
      case 'leads':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <LeadsPage
              initialLeadId={pane === 'primary' ? leadsRouteId : null}
              onLeadSelected={(leadId) => {
                // Only the main (left) page drives the address bar.
                if (pane !== 'primary') return;
                historyModeRef.current = leadId ? 'push' : 'replace';
                setLeadsRouteId(leadId);
              }}
            />
          </Suspense>
        );
      case 'contacts':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ContactsPage
              initialContactId={pane === 'primary' ? contactsRouteId : null}
              onContactSelected={(contactId) => {
                if (pane !== 'primary') return;
                historyModeRef.current = contactId ? 'push' : 'replace';
                setContactsRouteId(contactId);
              }}
            />
          </Suspense>
        );
      case 'invoices':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <InvoicesPage />
          </Suspense>
        );
      case 'estimates':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <EstimatesPage />
          </Suspense>
        );
      case 'technicians':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <TechniciansPage />
          </Suspense>
        );
      case 'settings':
        if (roleLoading) {
          return (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="text-gray-600 mt-2">Loading...</p>
            </div>
          );
        }
        if (!isAdmin) {
          return (
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600">You don't have permission to access this page.</p>
            </div>
          );
        }
        return (
          <Suspense fallback={<LoadingFallback />}>
            <SettingsPage />
          </Suspense>
        );
      case 'profile':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ProfilePage />
          </Suspense>
        );
      case 'notifications':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <NotificationsPage />
          </Suspense>
        );
      case 'email':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <EmailPage />
          </Suspense>
        );
      case 'map':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <MapPage />
          </Suspense>
        );
      case 'calendar':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CalendarPage />
          </Suspense>
        );
      case 'payment-provider':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PaymentProviderPage />
          </Suspense>
        );
      case 'reports':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ReportsPage />
          </Suspense>
        );
      case 'expenses':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ExpensesPage />
          </Suspense>
        );
      case 'dashboard':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CompanyDashboardPage />
          </Suspense>
        );
      case 'my-dashboard':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <TechnicianDashboardPage />
          </Suspense>
        );
      case 'service-catalog':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ServiceCatalogContent />
          </Suspense>
        );
      case 'messaging':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <MessagingPage />
          </Suspense>
        );
      case 'voip':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <VoipCallHistoryPage />
          </Suspense>
        );
      case 'permissions':
        if (roleLoading) {
          return (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="text-gray-600 mt-2">Loading...</p>
            </div>
          );
        }
        if (!isAdmin) {
          return (
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600">You don't have permission to access this page.</p>
            </div>
          );
        }
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PermissionsPage />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<LoadingFallback />}>
            <JobsPage />
          </Suspense>
        );
    }
  };

  // A locked subscription replaces the whole app with the subscribe screen. Everything else
  // (menus, routes, data) stays exactly as it is so the account comes straight back on renewal.
  if (isLocked) {
    return <SubscriptionLockedScreen onSignOut={confirmLogout} />;
  }

  return (
    <div className="screen-container bg-secondary-50 flex flex-col h-dvh">
      {/* Collapsible sidebar — hidden at xl+ (1280px). Covers phones and all iPad portrait widths
          (incl. 13" Pro at ~1032px) without JS device sniffing. */}
      <div className={`fixed inset-0 z-50 xl:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-slate-50 shadow-2xl animate-slide-in-left safe-area-y safe-area-left min-h-0">
          <div className="flex h-12 shrink-0 items-center justify-between pl-4 pr-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <img src="/favicon.png" alt="" className="h-9 w-9 -ml-1" />
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Service Strike</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Global Company Selector */}
          <div className="px-4 py-3 shrink-0 border-b border-slate-200">
            {companyPicker}
          </div>
          <ScrollableNav>
            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : renderNavItems(() => setSidebarOpen(false))}
          </ScrollableNav>
          {(isFeatureAllowed('jobs') || isFeatureAllowed('voip.call')) && (
            <div className="px-3 py-2 shrink-0 flex">
              <NewActionButton
                canCreateJob={isFeatureAllowed('jobs')}
                canCall={isFeatureAllowed('voip.call')}
                onNewJob={() => {
                  setSidebarOpen(false);
                  setIsNewJobModalOpen(true);
                }}
                onNewCall={() => {
                  setSidebarOpen(false);
                  setIsDialerOpen(true);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Persistent sidebar — only at xl+ so iPad portrait never gets the fixed rail */}
      <div className={`hidden xl:fixed xl:inset-y-0 xl:flex xl:flex-col xl:transition-[width] xl:duration-200 ${sidebarCollapsed ? 'xl:w-16' : 'xl:w-64'}`}>
        {/* safe-area-top applies the same top inset (status bar, call bar, offline bar) as
            .screen-container, so the logo header stays level with the top bar. */}
        <div className="flex flex-col flex-1 bg-slate-50 border-r border-slate-200 shadow-sm safe-area-left safe-area-top min-h-0">
          {sidebarCollapsed ? (
            <>
              <div className="flex h-12 shrink-0 items-center justify-center border-b border-slate-200">
                <img src="/favicon.png" alt="Service Strike" className="h-9 w-9" />
              </div>
              <div className="flex shrink-0 justify-center py-2 border-b border-slate-200">
                <button
                  type="button"
                  onClick={toggleSidebarCollapsed}
                  title="Expand menu"
                  aria-label="Expand menu"
                  className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors duration-200"
                >
                  <ChevronsRight className="h-5 w-5" />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex h-12 shrink-0 items-center gap-2 pl-4 pr-2 border-b border-slate-200">
                <img src="/favicon.png" alt="" className="h-9 w-9 -ml-1" />
                <h1 className="flex-1 min-w-0 truncate text-xl font-bold text-slate-900 tracking-tight">Service Strike</h1>
                <button
                  type="button"
                  onClick={toggleSidebarCollapsed}
                  title="Collapse menu"
                  aria-label="Collapse menu"
                  className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors duration-200"
                >
                  <ChevronsLeft className="h-5 w-5" />
                </button>
              </div>
              {/* Global Company Selector */}
              <div className="px-4 py-3 shrink-0 border-b border-slate-200">
                {companyPicker}
              </div>
            </>
          )}
          <ScrollableNav>
            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : renderNavItems(undefined, sidebarCollapsed)}
          </ScrollableNav>
          {(isFeatureAllowed('jobs') || isFeatureAllowed('voip.call')) && (sidebarCollapsed ? (
            <div className="py-2 shrink-0 flex justify-center">
              <NewActionButton
                canCreateJob={isFeatureAllowed('jobs')}
                canCall={isFeatureAllowed('voip.call')}
                onNewJob={() => setIsNewJobModalOpen(true)}
                onNewCall={() => setIsDialerOpen(true)}
              />
            </div>
          ) : (
            <div className="px-3 pb-4 pt-2 shrink-0 flex gap-2">
              {isFeatureAllowed('jobs') && (
                <button
                  onClick={() => setIsNewJobModalOpen(true)}
                  className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-semibold text-white whitespace-nowrap bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 rounded-xl shadow-lg shadow-primary-500/30 transition-all duration-200"
                >
                  <Plus className="h-5 w-5 flex-shrink-0" />
                  New Job
                </button>
              )}
              {isFeatureAllowed('voip.call') && (
                <button
                  onClick={() => setIsDialerOpen(true)}
                  className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-semibold text-white whitespace-nowrap bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 rounded-xl shadow-lg shadow-primary-500/30 transition-all duration-200"
                >
                  <Plus className="h-5 w-5 flex-shrink-0" />
                  New Call
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-0 xl:transition-[padding] xl:duration-200 ${sidebarCollapsed ? 'xl:pl-16' : 'xl:pl-64'}`}>
        {/* Top bar on every breakpoint: hamburger (below xl), page title, notification bell.
            Future global actions go next to the bell and profile menu. */}
        {/* Same bg-slate-50 as the sidebar. The border sits inside the h-12 row (48px total, like the
            sidebar logo header) so the two bottom borders line up. */}
        <div className="z-40 bg-slate-50 shadow-sm flex-shrink-0">
          <div className="relative flex h-12 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 border-b border-slate-200">
            <button
              type="button"
              className="p-2 -m-2 text-gray-700 hover:text-gray-900 hover:bg-slate-200/60 rounded-lg transition-colors duration-200 xl:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Split screen: title, timezone and clock stay within the left side's width so the right
                side's page picker can sit above the right page. */}
            <div
              className={`flex min-w-0 flex-1 items-center gap-x-4 sm:gap-x-6 ${splitTopBar ? 'overflow-hidden' : ''}`}
              style={splitTopBar ? { maxWidth: `min(calc(${splitRatio * 100}% - 36px), calc(100% - 470px))` } : undefined}
            >
            <TopBarTitle text={activeTab === 'notifications' ? 'Notifications' : activeTab === 'profile' ? 'Profile' : tabs.find(tab => tab.id === activeTab)?.label} />
            {/* Phones show the company clock (below) instead of the timezone label */}
            <TimezoneIndicator mode={tzIndicatorMode} className="text-gray-600 shrink-0 hidden sm:inline-flex" />
            {/* Phones, company time differs: local and company time stacked */}
            {showCompanyTime && (
              <div className="sm:hidden flex shrink-0 items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="leading-tight">
                  <div title="Local time">{localTime}</div>
                  <div className="text-slate-400">Company <span className="text-slate-600">{companyTime}</span></div>
                </div>
              </div>
            )}
            {/* Local time always; company time too on wider screens when it differs */}
            <div className={`${showCompanyTime ? 'hidden sm:flex' : 'flex'} shrink-0 items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap`}>
              <Clock className="h-4 w-4 text-slate-400 shrink-0" />
              <span title="Local time">{localTime}</span>
              {showCompanyTime && (
                <span className="hidden sm:inline text-slate-400">
                  · Company <span className="text-slate-600">{companyTime}</span>
                </span>
              )}
            </div>
            </div>
            {/* Split screen: the right side's page picker, at the right page's left edge (follows the
                divider; held back from the Split / bell / profile buttons when dragged far right). */}
            {splitTopBar && (
              <div
                className="absolute inset-y-0 flex items-center"
                style={{ left: `min(calc(${splitRatio * 100}% + 18px), calc(100% - 430px))` }}
              >
                <div className="w-48">
                  <CustomSelect
                    value={splitTab}
                    onChange={(value) => setSplitTab(value as TabType)}
                    options={tabs.map((tab) => ({ value: tab.id, label: tab.label }))}
                    placeholder="Choose a page"
                  />
                </div>
              </div>
            )}
            {isWideScreen && (
              <button
                type="button"
                onClick={() => setSplitOpen((open) => !open)}
                className={`${splitTopBar ? 'ml-auto ' : ''}inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors ${splitOpen ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-900'}`}
                aria-pressed={splitOpen}
                title={splitOpen ? 'Close split screen' : 'Split screen: show a second page on the right'}
              >
                <Columns className="h-4 w-4 text-blue-600" />
                Split
              </button>
            )}
            <NotificationBell />
            <ProfileMenu onLogout={() => setShowLogoutConfirm(true)} />
          </div>
        </div>

        {/* Read-only / trial-countdown strip. Renders nothing for a healthy subscription. */}
        <SubscriptionBanner />

        {/* Page content: one page, or two side by side in split screen */}
        {/* The left page keeps the same place in the tree with split open or closed, so toggling split
            (or the window crossing 1440px) doesn't remount it and throw away an open job's edits. */}
        <div ref={splitContainerRef} className="flex flex-1 min-h-0">
          <SplitScreenAreaContext.Provider value={splitTopBar ? primaryPaneRef : null}>
          <div ref={primaryPaneRef} className="flex min-w-0 flex-col" style={{ flex: splitTopBar ? `0 0 ${splitRatio * 100}%` : '1 1 0%' }}>
            {renderPage(activeTab, 'primary')}
          </div>
          </SplitScreenAreaContext.Provider>
          {splitTopBar && (<>
            {/* Drag to resize the two sides; double-click to make them equal again. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize split screen"
              title="Drag to resize, double-click to reset"
              onPointerDown={startSplitDrag}
              onDoubleClick={() => setSplitRatio(0.5)}
              className={`group relative z-10 flex w-1.5 shrink-0 cursor-col-resize touch-none items-center justify-center transition-colors ${isDraggingSplit ? 'bg-blue-500' : 'bg-slate-200 hover:bg-blue-400'}`}
            >
              <span className={`h-10 w-1 rounded-full transition-colors ${isDraggingSplit ? 'bg-white' : 'bg-slate-400 group-hover:bg-white'}`} />
            </div>
            <SplitScreenAreaContext.Provider value={secondaryPaneRef}>
            <SecondaryPaneContext.Provider value={true}>
            <div ref={secondaryPaneRef} className="flex min-w-0 flex-1 flex-col bg-slate-50/40">
              {renderPage(tabs.some((tab) => tab.id === splitTab) ? splitTab : 'jobs', 'secondary')}
            </div>
            </SecondaryPaneContext.Provider>
            </SplitScreenAreaContext.Provider>
          </>)}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        isLoading={loggingOut}
        title="Log out?"
        message={<p>{destructiveCopy.logout}</p>}
        confirmText="Log out"
      />

      <NewJobModal
        isOpen={isNewJobModalOpen}
        onClose={() => setIsNewJobModalOpen(false)}
        onJobCreated={() => {
          setIsNewJobModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['jobs'] });
        }}
      />

      <DialerModal isOpen={isDialerOpen} onClose={closeDialer} />

      {autoSetupWizard && (
        <Suspense fallback={null}>
          <SetupWizardModal onClose={() => setAutoSetupWizard(false)} />
        </Suspense>
      )}
    </div>
  );
}
